import React, { useCallback, useState, useEffect } from 'react';
import { Box, TextField, Button, MenuItem } from '@mui/material';
import CommonDatePicker from '../common/util/CommonDatePicker';
import * as carApi from '../../lib/api/car';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';

export const CarSearchBox = ({ handleAdd, formik, setSearchKeyword, searchResults }) => {
    const [carAssets, setCarAssets] = useState([]);

    useEffect(() => {
        let isMounted = true;
        const fetchAssets = async () => {
            try {
                const response = await carApi.getCarAssetList();
                if (isMounted) {
                    setCarAssets(response.data.list || []);
                }
            } catch (error) {
                console.error("차량 목록 호출 실패:", error);
            }
        };
        fetchAssets();
        return () => { isMounted = false; };
    }, []);

    const handleExcelDownload = async () => {
        const params = {
            searchKeyword: formik.values.carUuid,
            searchStrtDt: format(new Date(formik.values.searchStrtDt), 'yyyyMMdd'),
            searchEndDt: format(new Date(formik.values.searchEndDt), 'yyyyMMdd'),
        };
        const response = await carApi.getCarUsageList(params);
        const callDataList = response.data.list || [];

        const dataList = [...callDataList].sort((a, b) => 
            new Date(a.startDate) - new Date(b.startDate)
        );
        
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('운행기록부');

        worksheet.columns = Array(22).fill({ width: 8 });

        const setStyle = (cell, isHeader = false) => {
            cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
            cell.border = {
                top: { style: 'thin' },
                left: { style: 'thin' },
                bottom: { style: 'thin' },
                right: { style: 'thin' }
            };
            if (isHeader) {
                cell.font = { bold: true };
            }
        };

        worksheet.mergeCells('B2:F5');
        const dateCell = worksheet.getCell('B2');

        const formatKoreanDate = (dateVal) => {
            if (!dateVal) return "    년    월    일";
            const d = new Date(dateVal);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}년 ${month}월 ${day}일`;
        };

        const startDtText = formatKoreanDate(formik.values.searchStrtDt);
        const endDtText = formatKoreanDate(formik.values.searchEndDt);

        dateCell.value = `${startDtText}\n~\n${endDtText}`;
        
        setStyle(dateCell);

        worksheet.mergeCells('G2:M5');
        const titleCell = worksheet.getCell('G2');
        titleCell.value = '업무용 승용차 운행기록부';
        titleCell.font = { size: 16, bold: true };
        setStyle(titleCell);

        [['N2:P3', '상 호 명', 'Q2:S3', '㈜에이치씨인포'],
        ['N4:P5', '사업자등록번호', 'Q4:S5', '215-87-68879']].forEach(([labelPos, labelVal, valPos, valVal]) => {
            worksheet.mergeCells(labelPos);
            const lCell = worksheet.getCell(labelPos.split(':')[0]);
            lCell.value = labelVal;
            setStyle(lCell);

            worksheet.mergeCells(valPos);
            const vCell = worksheet.getCell(valPos.split(':')[0]);
            vCell.value = valVal;
            setStyle(vCell);
        });

        worksheet.getCell('B7').value = '1. 기본정보';
        worksheet.getCell('B7').font = { bold: true };

        worksheet.mergeCells('B8:F8');
        const carNoLabel = worksheet.getCell('B8');
        carNoLabel.value = '차량번호';
        setStyle(carNoLabel);

        worksheet.mergeCells('B9:F9');
        const carNoValue = worksheet.getCell('B9');
        const selectedCar = carAssets.find(car => car.carUuid === formik.values.carUuid);
        carNoValue.value = selectedCar
            ? `${selectedCar.carNumber} (${selectedCar.carModel})`
            : '전체';

        carNoValue.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF00' } };
        setStyle(carNoValue);
        
        worksheet.getCell('B11').value = '2. 업무용 사용비율 계산';
        worksheet.getCell('B11').font = { bold: true };

        const headers = [
            { m: 'B12:B14', v: '사용일자\n(요일)' },
            { m: 'C12:F12', v: '사용자' },
            { m: 'C13:D14', v: '부서' },
            { m: 'E13:F14', v: '성명' },
            { m: 'G12:G12', v: '주유내역' },
            { m: 'G13:G14', v: '주유비' },
            { m: 'H12:S12', v: '운행 내역' },
            { m: 'H13:I14', v: '주행 전\n계기판의 거리(km)', y: true },
            { m: 'J13:K14', v: '주행 후\n계기판의 거리(km)' },
            { m: 'L13:L14', v: '주행거리(km)' },
            { m: 'M13:M14', v: '방문 거래처명', y: true },
            { m: 'N13:R13', v: '업무용 사용거리(km)' },
            { m: 'N14:P14', v: '출퇴근용' },
            { m: 'Q14:R14', v: '일반 업무용' },
            { m: 'S13:S14', v: '비고' },
        ];

        headers.forEach(h => {
            worksheet.mergeCells(h.m);
            const cell = worksheet.getCell(h.m.split(':')[0]);
            cell.value = h.v;
            setStyle(cell, true);
            if (h.y) {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFF00' } };
            }
        });

        const columnWidths = {
            B: 16, // 사용일자
            C: 6, D: 6, // 부서 (병합된 C:D)
            E: 5, F: 5, // 성명 (병합된 E:F)
            G: 12, // 주유비
            H: 9, I: 9, // 주행 전 (병합된 H:I)
            J: 9, K: 9, // 주행 후 (병합된 J:K)
            L: 12, // 주행거리
            M: 20, // 방문 거래처명 (길게 설정)
            N: 5, O: 5, P: 6, // 출퇴근용 (N:P)
            Q: 8, R: 8, // 일반 업무용 (Q:R)
            S: 10, // 비고
        };

        // 반복문을 통해 컬럼 너비 적용
        Object.keys(columnWidths).forEach(col => {
            worksheet.getColumn(col).width = columnWidths[col];
        });

        let currentRow = 15;
        dataList.forEach((item) => {
            worksheet.mergeCells(`C${currentRow}:D${currentRow}`);
            worksheet.mergeCells(`E${currentRow}:F${currentRow}`);
            worksheet.mergeCells(`G${currentRow}:G${currentRow}`);
            worksheet.mergeCells(`H${currentRow}:I${currentRow}`);
            worksheet.mergeCells(`J${currentRow}:K${currentRow}`);
            worksheet.mergeCells(`L${currentRow}:L${currentRow}`);
            worksheet.mergeCells(`M${currentRow}:M${currentRow}`);
            worksheet.mergeCells(`N${currentRow}:P${currentRow}`);
            worksheet.mergeCells(`Q${currentRow}:R${currentRow}`);
            worksheet.mergeCells(`S${currentRow}:S${currentRow}`);

            const before = Number(item.beforeMileage) || 0;
            const after = Number(item.afterMileage) || 0;
            const driveDist = after - before;

            worksheet.getCell(`B${currentRow}`).value = item.startDate.split(' ')[0];
            worksheet.getCell(`C${currentRow}`).value = item.dptNm;
            worksheet.getCell(`E${currentRow}`).value = item.usrNm;
            worksheet.getCell(`G${currentRow}`).value = item.oilFee || 0;
            worksheet.getCell(`H${currentRow}`).value = item.beforeMileage;
            worksheet.getCell(`J${currentRow}`).value = item.afterMileage;
            worksheet.getCell(`L${currentRow}`).value = driveDist;
            worksheet.getCell(`M${currentRow}`).value = item.visitPartnerNm;
            worksheet.getCell(`N${currentRow}`).value = item.commuteMileage;
            worksheet.getCell(`Q${currentRow}`).value = item.businessMileage;
            worksheet.getCell(`S${currentRow}`).value = item.comment;

            const row = worksheet.getRow(currentRow);
            for (let i = 2; i <= 19; i++) { 
                const cell = row.getCell(i);
                setStyle(cell);
            }

            currentRow++;
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `운행기록부_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };


    const onChangeDate = useCallback((newValue, name) => {
        formik.setFieldValue(name, newValue);
    }, [formik]);

    const handleCarChange = useCallback((e) => {
        const value = e.target.value;
        formik.setFieldValue('carUuid', value);
        setSearchKeyword(value);
    }, [formik.setFieldValue, setSearchKeyword]);

    return (
        <Box sx={{ my: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box
                component="form"
                noValidate
                autoComplete="off"
                onSubmit={formik.handleSubmit}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <TextField
                    variant="standard"
                    select
                    name="carUuid"
                    label="배차 차량"
                    value={formik.values.carUuid || ''}
                    onChange={handleCarChange}
                    sx={{ width: '180px' }}
                >
                    <MenuItem value="">전체</MenuItem>
                    {carAssets.map((car) => (
                        <MenuItem key={car.carUuid} value={car.carUuid}>
                            {car.carNumber} ({car.carModel})
                        </MenuItem>
                    ))}
                </TextField>

                <CommonDatePicker label="시작일 시작 범위" name="searchStrtDt" value={formik.values.searchStrtDt} formik={formik} onChange={onChangeDate} />
                <CommonDatePicker label="시작일 끝 범위" name="searchEndDt" value={formik.values.searchEndDt} formik={formik} onChange={onChangeDate} />

                <Button type="submit" color="info" variant="contained" sx={{ ml: 1, minWidth: '80px', fontWeight: 700 }}>
                    검색
                </Button>
                <Button
                    onClick={handleExcelDownload}
                    color="success"
                    variant="contained"
                    sx={{ ml: 1, minWidth: '80px', fontWeight: 700 }}
                >
                    엑셀 출력
                </Button>
            </Box>
        </Box>
    );
};
